import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { marked } from 'marked';
import {
  BookOpen, FolderOpen, FileText, ChevronRight,
  Search, RefreshCw, AlertCircle, BookMarked,
} from 'lucide-react';
import { BACKEND_URL } from '@/config';

const API = BACKEND_URL + '/api';
axios.defaults.withCredentials = true;

// ─── marked config ────────────────────────────────────────────────────────────
marked.setOptions({ breaks: true, gfm: true });

// ─── Couleurs par section ─────────────────────────────────────────────────────
const SECTION_META = {
  docs:       { color: '#1d4ed8', bg: '#eff6ff', label: 'Docs',       icon: BookOpen },
  governance: { color: '#6d28d9', bg: '#f5f3ff', label: 'Gouvernance', icon: BookMarked },
};

// ─── Helpers ──────────────────────────────────────────────────────────────────
function subdir(path) {
  const parts = path.split('/');
  return parts.length > 2 ? parts[1] : null;
}

function groupFiles(files) {
  const groups = {};
  for (const f of files) {
    const key = f.section;
    if (!groups[key]) groups[key] = {};
    const sub = subdir(f.path) || '__root__';
    if (!groups[key][sub]) groups[key][sub] = [];
    groups[key][sub].push(f);
  }
  return groups;
}

function fmtSize(bytes) {
  if (bytes < 1024) return `${bytes} o`;
  return `${(bytes / 1024).toFixed(1)} Ko`;
}

function shortName(name) {
  return name.replace(/^\d+_/, '').replace(/\.md$/, '').replace(/_/g, ' ');
}

// ─── Composant principal ──────────────────────────────────────────────────────
export default function AdminDocs() {
  const [files, setFiles]         = useState([]);
  const [groups, setGroups]       = useState({});
  const [selected, setSelected]   = useState(null);
  const [content, setContent]     = useState('');
  const [loadingList, setLoadingList] = useState(true);
  const [loadingFile, setLoadingFile] = useState(false);
  const [error, setError]         = useState('');
  const [search, setSearch]       = useState('');
  const [expanded, setExpanded]   = useState({ docs: true, governance: true });

  // ── Charger la liste ───────────────────────────────────────────────────────
  const loadList = useCallback(async () => {
    setLoadingList(true);
    setError('');
    try {
      const { data } = await axios.get(`${API}/admin/docs`);
      setFiles(data.files);
      setGroups(groupFiles(data.files));
    } catch {
      setError('Impossible de charger la liste des fichiers.');
    } finally {
      setLoadingList(false);
    }
  }, []);

  useEffect(() => { loadList(); }, [loadList]);

  // ── Charger un fichier ────────────────────────────────────────────────────
  const loadFile = useCallback(async (file) => {
    if (selected?.path === file.path) return;
    setSelected(file);
    setLoadingFile(true);
    setContent('');
    try {
      const { data } = await axios.get(`${API}/admin/docs/file`, { params: { path: file.path } });
      setContent(data.content);
    } catch {
      setContent('');
      setError(`Impossible de charger : ${file.path}`);
    } finally {
      setLoadingFile(false);
    }
  }, [selected]);

  // ── Filtrage ──────────────────────────────────────────────────────────────
  const q = search.trim().toLowerCase();
  const filtered = q ? files.filter(f => f.name.toLowerCase().includes(q) || f.path.toLowerCase().includes(q)) : null;

  // ── Rendu liste ──────────────────────────────────────────────────────────
  function FileItem({ file }) {
    const active = selected?.path === file.path;
    return (
      <button
        onClick={() => loadFile(file)}
        style={{
          width: '100%', textAlign: 'left', padding: '7px 12px',
          background: active ? '#0e1b330d' : 'transparent',
          border: 'none', borderRadius: 6, cursor: 'pointer',
          display: 'flex', alignItems: 'center', gap: 8,
          borderLeft: active ? '3px solid var(--navy, #0e1b33)' : '3px solid transparent',
          transition: 'all .12s',
        }}
      >
        <FileText size={13} color={active ? '#0e1b33' : '#94a3b8'} />
        <span style={{
          fontSize: 12.5, fontWeight: active ? 600 : 400,
          color: active ? '#0e1b33' : '#374151',
          flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>
          {shortName(file.name)}
        </span>
        <span style={{ fontSize: 10, color: '#94a3b8', flexShrink: 0 }}>{fmtSize(file.size_bytes)}</span>
      </button>
    );
  }

  function SectionBlock({ sectionKey }) {
    const meta = SECTION_META[sectionKey] || { color: '#374151', bg: '#f8fafc', label: sectionKey, icon: FolderOpen };
    const Icon = meta.icon;
    const subs = groups[sectionKey] || {};
    const isOpen = expanded[sectionKey];

    return (
      <div style={{ marginBottom: 8 }}>
        <button
          data-testid={`section-btn-${sectionKey}`}
          onClick={() => setExpanded(e => ({ ...e, [sectionKey]: !e[sectionKey] }))}
          style={{
            width: '100%', textAlign: 'left', padding: '8px 10px',
            background: meta.bg, border: 'none', borderRadius: 6,
            cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8,
          }}
        >
          <Icon size={14} color={meta.color} />
          <span style={{ fontWeight: 700, fontSize: 12, color: meta.color, flex: 1, textTransform: 'uppercase', letterSpacing: '.04em' }}>
            {meta.label}
          </span>
          <ChevronRight size={13} color={meta.color} style={{ transform: isOpen ? 'rotate(90deg)' : 'none', transition: 'transform .15s' }} />
        </button>

        {isOpen && Object.entries(subs).map(([sub, subFiles]) => (
          <div key={sub} style={{ paddingLeft: sub === '__root__' ? 0 : 8 }}>
            {sub !== '__root__' && (
              <div style={{ padding: '5px 12px 2px', fontSize: 10.5, fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '.06em' }}>
                {sub}/
              </div>
            )}
            {subFiles.map(f => <FileItem key={f.path} file={f} />)}
          </div>
        ))}
      </div>
    );
  }

  // ─── Rendu principal ───────────────────────────────────────────────────────
  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', gap: 0 }}>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 800, color: '#0e1b33', margin: 0 }}>Documentation</h1>
          <p style={{ fontSize: 13, color: '#6b7280', margin: '2px 0 0' }}>
            {files.length} fichiers · docs/ et governance/
          </p>
        </div>
        <button onClick={loadList} disabled={loadingList} style={{
          display: 'flex', alignItems: 'center', gap: 6,
          padding: '7px 14px', borderRadius: 8, border: '1px solid #e5e7eb',
          background: '#fff', cursor: 'pointer', fontSize: 12.5, color: '#374151',
        }}>
          <RefreshCw size={13} style={{ animation: loadingList ? 'spin 1s linear infinite' : 'none' }} />
          Actualiser
        </button>
      </div>

      {error && (
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', padding: '10px 14px', background: '#fef2f2', borderRadius: 8, color: '#b91c1c', fontSize: 13, marginBottom: 16 }}>
          <AlertCircle size={15} /> {error}
        </div>
      )}

      {/* Layout 2 colonnes */}
      <div style={{ display: 'flex', gap: 16, flex: 1, minHeight: 0 }}>

        {/* ── Sidebar fichiers ─────────────────────────────────────── */}
        <aside style={{
          width: 260, flexShrink: 0,
          background: '#fff', borderRadius: 12, border: '1px solid #e5e7eb',
          display: 'flex', flexDirection: 'column', overflow: 'hidden',
        }}>
          {/* Recherche */}
          <div style={{ padding: '12px 12px 8px', borderBottom: '1px solid #f1f5f9' }}>
            <div style={{ position: 'relative' }}>
              <Search size={13} style={{ position: 'absolute', left: 9, top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }} />
              <input
                value={search} onChange={e => setSearch(e.target.value)}
                placeholder="Rechercher…"
                style={{
                  width: '100%', padding: '6px 8px 6px 28px',
                  border: '1px solid #e5e7eb', borderRadius: 7,
                  fontSize: 12.5, outline: 'none', boxSizing: 'border-box',
                  background: '#f8fafc',
                }}
              />
            </div>
          </div>

          {/* Liste */}
          <div style={{ flex: 1, overflowY: 'auto', padding: '8px 6px' }}>
            {loadingList ? (
              <div style={{ padding: 20, textAlign: 'center', color: '#94a3b8', fontSize: 12 }}>Chargement…</div>
            ) : filtered ? (
              filtered.length === 0
                ? <div style={{ padding: 16, textAlign: 'center', color: '#94a3b8', fontSize: 12 }}>Aucun résultat</div>
                : filtered.map(f => <FileItem key={f.path} file={f} />)
            ) : (
              Object.keys(groups).sort().map(s => <SectionBlock key={s} sectionKey={s} />)
            )}
          </div>
        </aside>

        {/* ── Contenu markdown ─────────────────────────────────────── */}
        <main style={{
          flex: 1, minWidth: 0,
          background: '#fff', borderRadius: 12, border: '1px solid #e5e7eb',
          display: 'flex', flexDirection: 'column', overflow: 'hidden',
        }}>
          {!selected ? (
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: '#94a3b8', gap: 12 }}>
              <BookOpen size={40} strokeWidth={1.2} />
              <span style={{ fontSize: 14 }}>Sélectionne un fichier pour l'afficher</span>
            </div>
          ) : (
            <>
              {/* Barre de titre fichier */}
              <div style={{
                padding: '14px 24px', borderBottom: '1px solid #f1f5f9',
                display: 'flex', alignItems: 'center', gap: 10,
                background: SECTION_META[selected.section]?.bg || '#f8fafc',
              }}>
                <span style={{
                  padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700,
                  background: SECTION_META[selected.section]?.color || '#374151',
                  color: '#fff', textTransform: 'uppercase', letterSpacing: '.05em',
                }}>
                  {SECTION_META[selected.section]?.label || selected.section}
                </span>
                <span style={{ fontSize: 13.5, fontWeight: 600, color: '#0e1b33', flex: 1 }}>
                  {selected.path}
                </span>
                <span style={{ fontSize: 11.5, color: '#94a3b8' }}>{fmtSize(selected.size_bytes)}</span>
              </div>

              {/* Rendu markdown */}
              <div style={{ flex: 1, overflowY: 'auto', padding: '24px 32px' }}>
                {loadingFile ? (
                  <div style={{ textAlign: 'center', color: '#94a3b8', paddingTop: 60 }}>Chargement…</div>
                ) : (
                  <div
                    className="md-render"
                    dangerouslySetInnerHTML={{ __html: marked.parse(content) }}
                  />
                )}
              </div>
            </>
          )}
        </main>
      </div>

      {/* Styles markdown inline */}
      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }

        .md-render { font-size: 14px; line-height: 1.75; color: #1e293b; }
        .md-render h1 { font-size: 22px; font-weight: 800; color: #0e1b33; margin: 0 0 20px; padding-bottom: 10px; border-bottom: 2px solid #e5e7eb; }
        .md-render h2 { font-size: 17px; font-weight: 700; color: #0e1b33; margin: 28px 0 10px; padding-bottom: 6px; border-bottom: 1px solid #f1f5f9; }
        .md-render h3 { font-size: 14.5px; font-weight: 700; color: #1e40af; margin: 20px 0 8px; }
        .md-render h4 { font-size: 13.5px; font-weight: 600; color: #374151; margin: 14px 0 6px; }
        .md-render p { margin: 0 0 12px; }
        .md-render a { color: #1d4ed8; text-decoration: underline; }
        .md-render strong { font-weight: 700; color: #0f172a; }
        .md-render em { font-style: italic; color: #475569; }
        .md-render code { font-family: 'Fira Mono', monospace; font-size: 12.5px; background: #f1f5f9; color: #be185d; padding: 1px 5px; border-radius: 4px; }
        .md-render pre { background: #0f172a; color: #e2e8f0; padding: 16px 20px; border-radius: 10px; overflow-x: auto; margin: 12px 0 18px; }
        .md-render pre code { background: none; color: inherit; padding: 0; font-size: 12.5px; }
        .md-render blockquote { border-left: 3px solid #6d28d9; background: #f5f3ff; padding: 10px 16px; margin: 14px 0; border-radius: 0 8px 8px 0; color: #4c1d95; font-style: italic; }
        .md-render ul, .md-render ol { padding-left: 22px; margin: 0 0 12px; }
        .md-render li { margin-bottom: 4px; }
        .md-render hr { border: none; border-top: 1px solid #e5e7eb; margin: 24px 0; }
        .md-render table { width: 100%; border-collapse: collapse; margin: 14px 0 20px; font-size: 13px; }
        .md-render thead { background: #0e1b33; color: #fff; }
        .md-render th { padding: 9px 14px; text-align: left; font-weight: 600; font-size: 12px; letter-spacing: .04em; }
        .md-render td { padding: 8px 14px; border-bottom: 1px solid #f1f5f9; }
        .md-render tr:nth-child(even) td { background: #f8fafc; }
        .md-render img { max-width: 100%; border-radius: 8px; }
      `}</style>
    </div>
  );
}
